import React, { useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { motion } from 'framer-motion';
import { 
  Send, 
  MapPin, 
  Phone, 
  Mail, 
  Clock, 
  CheckCircle2,
  ArrowUpRight
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const Contact = () => {
  const { t, currentLanguage } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    subject: '',
    message: '',
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await axios.post(`${API}/contact`, {
        ...formData,
        language: currentLanguage
      });
      setSubmitted(true);
      toast.success(t('contact.form.success'));
    } catch (error) {
      console.error('Contact submission failed:', error);
      toast.error(t('contact.form.error'));
    } finally {
      setLoading(false);
    }
  };

  const contactInfo = [
    {
      icon: MapPin,
      title: t('contact.info.address'),
      value: t('contact.info.address_value'),
    },
    {
      icon: Phone,
      title: t('contact.info.phone'),
      value: t('contact.info.phone_value'),
      href: 'https://wa.me/905377020927',
    },
    {
      icon: Mail,
      title: t('contact.info.email'),
      value: t('contact.info.email_value'),
      href: 'mailto:info@varyaztrade.com',
    },
    {
      icon: Clock,
      title: t('contact.info.hours'),
      value: t('contact.info.hours_value'),
    },
  ];

  if (submitted) {
    return (
      <div className="min-h-screen bg-[#f9f9f7] pt-24 flex items-center justify-center" data-testid="contact-success">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="max-w-lg mx-auto text-center px-6"
        >
          <div className="w-24 h-24 bg-[#0a1628] flex items-center justify-center mx-auto mb-8">
            <CheckCircle2 className="w-12 h-12 text-secondary-500" />
          </div>
          <h2 className="text-3xl md:text-4xl font-heading font-bold text-primary-900 mb-4">
            Message Sent
          </h2>
          <p className="text-muted-foreground mb-10 leading-relaxed">
            Thank you for reaching out. Our team will respond within 24 hours.
          </p>
          <Button
            onClick={() => {
              setSubmitted(false);
              setFormData({ name: '', email: '', company: '', subject: '', message: '' });
            }}
            className="bg-[#0a1628] hover:bg-primary-800 text-white rounded-none px-10 py-6 text-xs font-bold uppercase tracking-widest"
          >
            Send Another Message
          </Button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f9f9f7]" data-testid="contact-page">
      {/* Header - Premium */}
      <section className="relative overflow-hidden pt-20 sm:pt-24">
        <div className="absolute inset-0 bg-gradient-to-br from-[#0a1628] via-[#0d1d35] to-[#0a1628]" />
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 right-0 w-1/2 h-full" style={{
            backgroundImage: `radial-gradient(circle at 70% 30%, rgba(197, 160, 89, 0.3) 0%, transparent 50%)`
          }} />
        </div>
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 md:px-12 py-12 sm:py-16 md:py-20 relative">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <span className="inline-flex items-center gap-3 text-secondary-500 mb-4">
              <span className="w-12 h-px bg-secondary-500" />
              <span className="text-xs font-mono uppercase tracking-[0.3em]">{t('contact.get_in_touch')}</span>
            </span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-heading font-bold text-white tracking-tight mb-6">
              {t('contact.title')}
            </h1>
            <p className="text-lg text-white/60 max-w-xl">
              {t('contact.subtitle')}
            </p>
          </motion.div>
        </div>
      </section>

      <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-16 md:py-24">
        <div className="grid lg:grid-cols-5 gap-12 lg:gap-20">
          {/* Contact Info - Premium Cards */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            className="lg:col-span-2 space-y-6"
          >
            {contactInfo.map((info, index) => (
              <div
                key={index}
                className="group flex items-start gap-5 p-6 bg-white border border-border hover:border-secondary-500/30 hover:shadow-lg transition-all duration-300"
                data-testid={`contact-info-${index}`}
              >
                <div className="w-14 h-14 bg-[#0a1628] flex items-center justify-center flex-shrink-0 group-hover:bg-secondary-500 transition-colors duration-300">
                  <info.icon className="w-5 h-5 text-secondary-500 group-hover:text-primary-900 transition-colors duration-300" strokeWidth={1.5} />
                </div>
                <div>
                  <p className="text-xs font-mono uppercase tracking-wider text-muted-foreground mb-1">{info.title}</p>
                  {info.href ? (
                    <a
                      href={info.href}
                      className="text-primary-900 font-medium hover:text-secondary-600 transition-colors"
                    >
                      {info.value}
                    </a>
                  ) : (
                    <p className="text-primary-900 font-medium">{info.value}</p>
                  )}
                </div>
              </div>
            ))}

            {/* Quick Contact CTA */}
            <div className="bg-secondary-500 p-8 mt-8">
              <h3 className="text-xl font-heading font-bold text-primary-900 mb-3">
                Need Immediate Help?
              </h3>
              <p className="text-primary-900/70 text-sm mb-6">
                Our team is available to assist with urgent inquiries.
              </p>
              <a href="https://wa.me/905377020927" target="_blank" rel="noopener noreferrer">
                <Button className="w-full bg-[#0a1628] hover:bg-primary-800 text-white rounded-none py-5 text-xs font-bold uppercase tracking-widest group">
                  WhatsApp Us
                  <ArrowUpRight className="w-4 h-4 ml-2 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </Button>
              </a>
            </div>
          </motion.div>

          {/* Contact Form */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            className="lg:col-span-3"
          >
            <div className="bg-white border border-border p-8 md:p-12">
              <h2 className="text-2xl font-heading font-bold text-primary-900 mb-8">
                Send us a message
              </h2>
              
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-3 block">
                      {t('contact.form.name')} *
                    </label>
                    <Input
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="John Doe"
                      required
                      className="h-14 rounded-none border-border"
                      data-testid="contact-name"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-3 block">
                      {t('contact.form.email')} *
                    </label>
                    <Input
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="your@email.com"
                      required
                      className="h-14 rounded-none border-border"
                      data-testid="contact-email"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-3 block">
                      {t('contact.form.company')}
                    </label>
                    <Input
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                      placeholder="Your company"
                      className="h-14 rounded-none border-border"
                      data-testid="contact-company"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-3 block">
                      {t('contact.form.subject')} *
                    </label>
                    <Input
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      placeholder="How can we help?"
                      required
                      className="h-14 rounded-none border-border"
                      data-testid="contact-subject"
                    />
                  </div>
                </div>

                <div>
                  <label className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-3 block">
                    {t('contact.form.message')} *
                  </label>
                  <Textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    placeholder="Tell us more about your inquiry..."
                    rows={6}
                    required
                    className="rounded-none border-border resize-none"
                    data-testid="contact-message"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  className="bg-[#0a1628] hover:bg-primary-800 text-white rounded-none px-12 py-6 text-xs font-bold uppercase tracking-widest"
                  data-testid="contact-submit"
                >
                  {loading ? (
                    <span className="flex items-center gap-2">
                      <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      Sending...
                    </span>
                  ) : (
                    <span className="flex items-center gap-2">
                      <Send className="w-4 h-4" />
                      {t('contact.form.submit')}
                    </span>
                  )}
                </Button>
              </form>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Map Placeholder */}
      <section className="h-96 bg-muted relative overflow-hidden">
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center">
            <div className="w-16 h-16 bg-[#0a1628] flex items-center justify-center mx-auto mb-4">
              <MapPin className="w-6 h-6 text-secondary-500" />
            </div>
            <p className="text-muted-foreground font-medium">Dnipro, Ukraine</p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
